import { BriefPage } from 'features/briefs/components/brief-page'
import React, { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { CHOOSE_RECIPIENTS_TAB } from 'features/briefs/utils/nav'
import Row from 'components/common/row'
import DataTable, { Column } from 'components/ui/data-table'
import Heading from 'components/ui/heading'
import CustomDropdown from 'components/ui/dropdowns/custom-dropdown'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { getEmailGroups } from '@disco/state-management/selectors/emailGroups'
import { Contact, RecipientListId } from 'types/list'
import {
  getSubscribedContactsInEmailGroup,
  isContactsLoaded,
} from '@disco/state-management/selectors/contacts-all'
import Button from 'components/ui/button'
import { Box, Callout, Flex, Text } from 'components/ds'
import {
  ContactsDropdown,
  ExtendedDropdownOption,
} from 'features/briefs/components/contacts-dropdown'
import UserAvatar from 'components/ui/user-avatar'
import {
  getBrief,
  getBriefContacts,
  updateBrief,
  removeRecipient,
  saveRecipients,
} from '@disco/state-management/reducers/entities/briefs'
import Spinner from 'components/ui/spinner'
import { MdInfoOutline } from '@react-icons/all-files/md/MdInfoOutline'
import { __REACT_NATIVE__ } from '@disco/constants/environment'
import type { BriefRecipient } from 'types/brief'
import { RemoveRecipientConfirmation } from 'features/briefs/components/remove-recipient-confirmation'
import mixpanel from '@disco/api/mixpanel'
const rowSizes = {
  3_9: [3, 9],
}

export const BriefEditorRecipientsPage = () => {
  const params = useParams<{ briefId: string }>()
  const dispatch = useDispatch()
  const brief = useSelector(state =>
    getBrief(state, { briefId: parseInt(params.briefId!) }),
  )

  const contactsLoaded = useSelector(isContactsLoaded)

  const briefContacts = useSelector(state =>
    getBriefContacts(state, { briefId: parseInt(params.briefId!) }),
  )
  const [selectedEmailGroup, setSelectedEmailGroup] =
    useState<RecipientListId | null>(null)

  const emailGroups = useSelector(getEmailGroups)

  const emailGroupRecipients = useSelector(state =>
    getSubscribedContactsInEmailGroup(state, { groupId: selectedEmailGroup }),
  )

  const [selectedContact, setSelectedContact] =
    useState<ExtendedDropdownOption | null>(null)

  const [recipientToRemove, setRecipientToRemove] =
    useState<BriefRecipient | null>(null)

  const onRemoveRecipient = async (
    contact: BriefRecipient,
    confirmed: boolean = false,
  ) => {
    if (!brief) return

    if (contact?.sent_at && !confirmed) {
      setRecipientToRemove(contact)
      return
    }

    if ('id' in contact) {
      await dispatch(
        removeRecipient({
          id: brief.id,
          recipientId: contact.id,
          forceSave: confirmed,
        }),
      )
    } else {
      await dispatch(
        removeRecipient({
          id: brief.id,
          email: contact.email,
          forceSave: confirmed,
        }),
      )
    }
    if (confirmed) {
      await dispatch(saveRecipients(brief.id))
    }
  }

  const columns: Column[] = useMemo(
    () => [
      {
        contentType: 'button',
        width: 'content',
        name: 'Name',
        render: (prop: BriefRecipient) => (
          <UserAvatar
            name={'name' in prop && prop.name ? prop.name : undefined}
            size="24px"
            fontSize="11px"
          />
        ),
      },
      {
        id: 'name',
      },
      {
        id: 'email',
        name: 'Email',
      },
      {
        contentType: 'button',
        width: 'content',
        name: '',
        render: (prop: BriefRecipient) => (
          <Button
            appearance="icon"
            caption="×"
            onClick={() => onRemoveRecipient(prop)}
          />
        ),
      },
    ],
    [onRemoveRecipient],
  )

  const onAddRecipients = (
    newRecipients: Contact[],
    emailGroupId: RecipientListId,
  ) => {
    if (!brief) return

    const updatedRecipients = [
      ...(brief.recipients_list || []),
      ...newRecipients
        .filter(
          r => !brief.recipients_list?.find(c => 'id' in c && c.id === r.id),
        )
        .map(recipient => ({
          id: recipient.id,
          saved_recipients_id: emailGroupId,
          selected: true,
        })),
    ]

    dispatch(
      updateBrief({
        ...brief,
        recipients_list: updatedRecipients,
      }),
    )
    mixpanel.sendPureEvent('Brief recipient added', {
      Context: 'Briefs',
      brief_id: brief.id,
      count: newRecipients.length,
    })
    setSelectedEmailGroup(null)
  }

  const onAddSingleRecipient = (
    contact: Contact | { type: 'email'; email: string },
  ) => {
    if (
      !brief ||
      ('name' in contact &&
        brief.recipients_list?.find(r => 'id' in r && r.id === contact.id)) ||
      ('type' in contact &&
        brief.recipients_list?.find(
          r => 'type' in r && r.type === 'email' && r.email === contact.email,
        ))
    )
      return

    const updatedRecipients: BriefRecipient[] = [
      ...(brief.recipients_list || []),
      'name' in contact
        ? {
            id: contact.id,
            saved_recipients_id: null,
            selected: true,
          }
        : {
            type: 'email',
            email: contact.email,
            selected: true,
          },
    ]

    dispatch(
      updateBrief({
        ...brief,
        recipients_list: updatedRecipients,
      }),
    )
    mixpanel.sendPureEvent('Brief recipient added', {
      Context: 'Briefs',
      brief_id: brief.id,
      count: 1,
    })
    setSelectedContact(null)
  }

  if (!brief) return null

  const dataTable = contactsLoaded ? (
    <div>
      {briefContacts.length === 0 ? (
        <Callout.Root color="gray">
          <Callout.Icon>
            <MdInfoOutline />
          </Callout.Icon>
          <Callout.Text>
            No recipients selected. Enter an email or select a mailing list to
            get started.
          </Callout.Text>
        </Callout.Root>
      ) : (
        <>
          <Heading size={500}>{briefContacts.length} contacts selected</Heading>
          <DataTable
            appearance="plain"
            columns={columns}
            data={briefContacts}
          />
        </>
      )}
    </div>
  ) : (
    <Spinner />
  )

  return (
    <BriefPage step={CHOOSE_RECIPIENTS_TAB} title={CHOOSE_RECIPIENTS_TAB}>
      <Row columnsSize={rowSizes[3_9]}>
        <Flex gap="3" direction="column">
          <Box>
            <Text size="2" weight="medium">
              Add a contact
            </Text>
            <Flex gap="1">
              <Box width="80%">
                <ContactsDropdown
                  value={selectedContact}
                  placeholder=""
                  onChange={setSelectedContact}
                />
              </Box>
              <Button
                appearance="primary"
                disabled={!selectedContact}
                caption={`+ Add`}
                onClick={() => onAddSingleRecipient(selectedContact.data)}
              />
            </Flex>
            <Text size="1" color="gray" as="div" mt="1">
              Enter an email address or search for a DISCO Contact
            </Text>
          </Box>

          <Box>
            <Text size="2" weight="medium">
              Add from mailing list
            </Text>
            <Flex gap="1">
              <Box width="80%">
                <CustomDropdown
                  options={emailGroups.map(item => ({
                    label: item.name,
                    value: item.id.toString(),
                  }))}
                  multi={false}
                  clearable={false}
                  placeholder="Select a mailing list"
                  value={selectedEmailGroup}
                  onChange={ruleValue => {
                    setSelectedEmailGroup(
                      ruleValue ? parseInt(ruleValue.value) : null,
                    )
                  }}
                />
              </Box>
              <Button
                appearance="primary"
                disabled={!selectedEmailGroup}
                caption={`+ Add`}
                onClick={() =>
                  onAddRecipients(emailGroupRecipients, selectedEmailGroup)
                }
              />
            </Flex>
          </Box>
        </Flex>
        {dataTable}
      </Row>
      {recipientToRemove && (
        <RemoveRecipientConfirmation
          onClose={removeRecipient => {
            if (removeRecipient) {
              onRemoveRecipient(recipientToRemove, true)
            }
            setRecipientToRemove(null)
          }}
        />
      )}
    </BriefPage>
  )
}
